import * as React from 'react';
import { Button, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { Settings16Regular } from '@fluentui/react-icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppState } from '../../../redux/reducers';
import { HCConcept } from '../../../models/Entities/Protocols/HCVisit';
import { StatusPill } from '../../../theme/components/StatusPill';
import VisitConceptStatusIcon from './VisitConceptStatusIcon';
import VisitConceptLeafModal from './VisitConceptLeafModal';
import * as Functions from '../VisitConcepts.Functions';

interface VisitConceptTreeLeafProps {
  ContractId: number;
  value: string;
  concept: HCConcept;
  formId: number;
  formName: string;
  /** Posición del concepto dentro del formulario (identifica al "realizado" de cada formulario). */
  itemNumber: number;
  active?: boolean;
  onActivate?: (value: string) => void;
}

/**
 * Hoja del árbol: estado (realizado / no), descripción, "campo pagable" como secundario y, a la derecha,
 * las pastillas Facturable / Prestación y el engranaje de configurar. El engranaje está siempre en el layout
 * (ocupa su lugar) y sólo se hace visible con el mouse encima o con foco: así la fila no se reacomoda.
 * No se usa el slot `actions` de Fluent porque se monta al pasar el mouse y desplaza el texto.
 */
const VisitConceptTreeLeaf: React.FC<VisitConceptTreeLeafProps> = ({ ContractId, value, concept, formId, formName, itemNumber, active, onActivate }) => {
  const { t } = useTranslation();
  const relationships = useSelector((state: AppState) => state.ContractConceptRelationship?.list);

  const label = Functions.conceptLabel(concept, t);
  const payable = Functions.isPayableConcept(concept, relationships, label, formName, itemNumber);
  const procedure = Functions.isProcedureConcept(concept, relationships, label, formName, itemNumber);
  const isPayableField = concept.type === Functions.PAYABLE_FIELD_TYPE;

  return (
    <TreeItem itemType="leaf" value={value} data-testid={value}>
      <TreeItemLayout
        iconBefore={<VisitConceptStatusIcon realized={!!concept.realized} />}
        data-active={active ? 'true' : undefined}
        onClick={() => onActivate?.(value)}
      >
        <span className="vct-node vct-node--leaf">
          <span className="vct-node__main">
            <span className={`vct-node__title${payable ? ' vct-node__title--strong' : ''}`}>{label}</span>
            {isPayableField && <span className="vct-node__secondary">{t('visitConcepts.tree.payableField')}</span>}
          </span>
          <span className="vct-aside">
            {payable && (
              <StatusPill tone="success" size="small" data-testid="vct-pill-payable">
                {t('visitConcepts.tree.payable')}
              </StatusPill>
            )}
            {procedure && (
              <StatusPill tone="info" size="small" data-testid="vct-pill-procedure">
                {t('visitConcepts.tree.procedure')}
              </StatusPill>
            )}
            {/* El click en el engranaje no debe plegar la rama ni cambiar el nodo activo. */}
            <span className="vct-gear" onClick={(event) => event.stopPropagation()}>
              <VisitConceptLeafModal
                ContractId={ContractId}
                formId={formId}
                formName={formName}
                itemNumber={itemNumber}
                label={label}
                realized={concept.realized}
                triggerAs={
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<Settings16Regular />}
                    aria-label={t('visitConcepts.tree.configure', { concept: label })}
                    title={t('visitConcepts.tree.configure', { concept: label })}
                    data-testid={`${value}-configure`}
                  />
                }
              />
            </span>
          </span>
        </span>
      </TreeItemLayout>
    </TreeItem>
  );
};

export default VisitConceptTreeLeaf;
